import { useCallback, useEffect, useRef, useSyncExternalStore } from 'react';

/**
 * Keyboard shortcuts (DESIGN.md §10). A combo is `mod+k`, `shift+/`, `n`, and so on.
 * `mod` is Ctrl on Windows and Linux and ⌘ on macOS.
 */
export interface ShortcutOptions {
  /**
   * Fire even while typing in a field. Only for combos with a modifier (like `mod+k`),
   * never for single keys that people type.
   */
  allowInFields?: boolean;
  enabled?: boolean;
  /**
   * What the shortcut does, in the words of the button it stands for ("Take payment").
   * Shortcuts with a description are listed in the "?" overlay while they are active.
   */
  description?: string;
}

/** A shortcut listed in the "?" overlay. */
export interface ActiveShortcut {
  combo: string;
  description: string;
}

// The shortcuts active on screen right now, for the "?" overlay. Mounting a component that
// calls useShortcut with a description adds one; unmounting removes it.
const active = new Map<symbol, ActiveShortcut>();
let snapshot: readonly ActiveShortcut[] = [];
const listeners = new Set<() => void>();

function publish(): void {
  const seen = new Set<string>();
  snapshot = [...active.values()].filter((s) => {
    const key = `${s.combo}|${s.description}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
  for (const listener of listeners) listener();
}

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

/** The described shortcuts currently active, in the order they were registered. */
export function useActiveShortcuts(): readonly ActiveShortcut[] {
  return useSyncExternalStore(
    subscribe,
    () => snapshot,
    () => snapshot,
  );
}

function isApplePlatform(): boolean {
  if (typeof navigator === 'undefined') return false;
  return /Mac|iPhone|iPad/.test(navigator.userAgent);
}

const KEY_LABELS: Record<string, string> = { escape: 'Esc', enter: 'Enter', ' ': 'Space' };

function keyLabel(key: string): string {
  const named = KEY_LABELS[key];
  if (named !== undefined) return named;
  if (key.length === 1) return key.toUpperCase();
  return key.charAt(0).toUpperCase() + key.slice(1);
}

/**
 * The keys as people read them: `mod+k` is "Ctrl K" (⌘K on a Mac), `?` is "?",
 * `n` is "N". Use it in hints and tooltips so every screen writes shortcuts the same way.
 */
export function formatShortcut(combo: string, apple: boolean = isApplePlatform()): string {
  const { key, mod, shift, alt } = parseCombo(combo);
  const keyName = keyLabel(key);
  // Shift is implied by the character for punctuation typed with Shift, like "?".
  const showShift = shift && /^[a-z0-9]$/.test(key);
  if (apple) {
    return `${mod ? '⌘' : ''}${alt ? '⌥' : ''}${showShift ? '⇧' : ''}${keyName}`;
  }
  return [mod ? 'Ctrl' : null, alt ? 'Alt' : null, showShift ? 'Shift' : null, keyName]
    .filter((part) => part !== null)
    .join(' ');
}

/** The `aria-keyshortcuts` value for a combo, e.g. `mod+k` → "Control+K Meta+K". */
export function ariaKeyShortcuts(combo: string): string {
  const { key, mod, shift, alt } = parseCombo(combo);
  const base = [alt ? 'Alt' : null, shift && /^[a-z0-9]$/.test(key) ? 'Shift' : null, keyLabel(key)]
    .filter((part) => part !== null)
    .join('+');
  return mod ? `Control+${base} Meta+${base}` : base;
}

/**
 * A ref that sets `aria-keyshortcuts` on an element. React Aria components drop unknown
 * aria attributes, so the attribute is set on the DOM node directly.
 */
export function useAriaKeyShortcuts(combo: string | undefined): (el: HTMLElement | null) => void {
  return useCallback(
    (el: HTMLElement | null) => {
      if (el === null) return;
      if (combo === undefined) el.removeAttribute('aria-keyshortcuts');
      else el.setAttribute('aria-keyshortcuts', ariaKeyShortcuts(combo));
    },
    [combo],
  );
}

interface ParsedCombo {
  key: string;
  mod: boolean;
  shift: boolean;
  alt: boolean;
}

export function parseCombo(combo: string): ParsedCombo {
  const parts = combo.toLowerCase().split('+');
  const key = parts.pop();
  if (key === undefined || key === '') {
    throw new Error(`Invalid shortcut "${combo}"`);
  }
  const mods = new Set(parts);
  for (const m of mods) {
    if (m !== 'mod' && m !== 'shift' && m !== 'alt') {
      throw new Error(`Unknown modifier "${m}" in shortcut "${combo}"`);
    }
  }
  return { key, mod: mods.has('mod'), shift: mods.has('shift'), alt: mods.has('alt') };
}

/** Punctuation like "?" needs Shift on some keyboard layouts and not on others. */
function isPunctuation(key: string): boolean {
  return key.length === 1 && !/^[a-z0-9]$/.test(key);
}

export function matchesCombo(event: KeyboardEvent, combo: ParsedCombo): boolean {
  const mod = event.ctrlKey || event.metaKey;
  return (
    event.key.toLowerCase() === combo.key &&
    mod === combo.mod &&
    (isPunctuation(combo.key) || event.shiftKey === combo.shift) &&
    event.altKey === combo.alt
  );
}

function isTypingTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  if (target.isContentEditable) return true;
  return (
    target instanceof HTMLInputElement ||
    target instanceof HTMLTextAreaElement ||
    target instanceof HTMLSelectElement
  );
}

export function useShortcut(
  combo: string,
  handler: (event: KeyboardEvent) => void,
  { allowInFields = false, enabled = true, description }: ShortcutOptions = {},
): void {
  const handlerRef = useRef(handler);
  useEffect(() => {
    handlerRef.current = handler;
  }, [handler]);

  useEffect(() => {
    if (!enabled) return undefined;
    const parsed = parseCombo(combo);
    if (allowInFields && !parsed.mod && !parsed.alt) {
      throw new Error(`Shortcut "${combo}" needs a modifier to be allowed in fields`);
    }
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.defaultPrevented || event.isComposing) return;
      if (!allowInFields && isTypingTarget(event.target)) return;
      if (!matchesCombo(event, parsed)) return;
      event.preventDefault();
      handlerRef.current(event);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => {
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [combo, allowInFields, enabled]);

  useEffect(() => {
    if (!enabled || description === undefined) return undefined;
    const id = Symbol(combo);
    active.set(id, { combo, description });
    publish();
    return () => {
      active.delete(id);
      publish();
    };
  }, [combo, enabled, description]);
}
